import React from "react";
import {
  Layers,
  PenTool,
  Rocket,
  PlugZap,
  ShieldCheck,
  Gauge,
} from "lucide-react";

const services = [
  {
    icon: Layers,
    title: "Native Android Development",
    desc:
      "Kotlin + Jetpack Compose. Modular architecture, MVI/MVVM, multi-module Gradle, the works.",
    big: true,
    span: "md:col-span-7 md:row-span-2",
    tone: "ink",
  },
  {
    icon: PenTool,
    title: "Product & UI/UX Design",
    desc:
      "Material 3 design systems with personality. Pixel-perfect, never generic.",
    span: "md:col-span-5",
    tone: "white",
  },
  {
    icon: Rocket,
    title: "Play Store Launch",
    desc:
      "Store listing, ASO, screenshots, staged rollouts and review prep.",
    span: "md:col-span-5",
    tone: "acid",
  },
  {
    icon: PlugZap,
    title: "Backend & API Integration",
    desc:
      "FastAPI, Node, Firebase, gRPC. Auth, payments, realtime, all of it.",
    span: "md:col-span-4",
    tone: "white",
  },
  {
    icon: Gauge,
    title: "Performance Optimization",
    desc:
      "Cold-start, frame-rate, battery. We measure obsessively.",
    span: "md:col-span-4",
    tone: "white",
  },
  {
    icon: ShieldCheck,
    title: "Maintenance & Support",
    desc:
      "Long-term partnerships. SLAs, updates, OS migration.",
    span: "md:col-span-4",
    tone: "ultra",
  },
];

const toneClasses = {
  ink: "border-ink bg-ink text-cream",
  white: "border-ink/10 bg-white text-ink",
  acid: "border-ink bg-acid text-ink",
  ultra: "border-ultra bg-ultra text-cream",
};

const ServiceCard = ({ s, idx }) => {
  const Icon = s.icon;
  const tone = toneClasses[s.tone];
  const isLight = s.tone === "white" || s.tone === "acid";

  return (
    <div
      data-testid={`service-card-${idx}`}
      className={`group relative flex flex-col justify-center overflow-hidden rounded-sm border p-7 transition-transform hover:-translate-y-1 md:p-9 ${tone} ${s.span}`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="h-8 w-8" strokeWidth={1.6} />

      </div>

      <div className="mt-6 md:mt-8">
        <div
          className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
            isLight ? "text-ink/50" : "text-cream/60"
          }`}
        >
          0{idx + 1} / Service
        </div>

        <h3
          className={`mt-2 font-display font-extrabold tracking-tightest ${
            s.big ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"
          }`}
        >
          {s.title}
        </h3>

        <p
          className={`mt-4 max-w-md font-body text-base leading-relaxed ${
            isLight ? "text-ink/70" : "text-cream/70"
          }`}
        >
          {s.desc}
        </p>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-cream py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="mb-14 grid grid-cols-12 items-end gap-6 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-ultra">
              02 — What we do
            </div>

            <h2 className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.92] tracking-tightest text-ink md:text-7xl">
              A studio that builds,
              <br />
              not <span className="italic text-ultra">decks.</span>
            </h2>
          </div>

          <div className="col-span-12 md:col-span-5">
            <p className="font-body text-lg leading-relaxed text-ink/70">
              We're a small, senior team. Strategy, design and engineering sit
              at the same table — which is how apps stop feeling stitched
              together.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:auto-rows-min md:grid-cols-12">
          {services.map((s, i) => (
            <ServiceCard key={s.title} s={s} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;